import { ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface MobileSidebarDrawerProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  showHeader?: boolean;
  showCloseButton?: boolean;
  footer?: ReactNode;
  children: ReactNode;
}

export function MobileSidebarDrawer({
  open,
  onClose,
  title = 'Menu',
  showHeader = true,
  showCloseButton = true,
  footer,
  children,
}: MobileSidebarDrawerProps) {
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 md:hidden">
      <div
        className="absolute inset-0 bg-white flex flex-col pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]"
      >
        {showHeader ? (
          <div className="h-14 border-b border-peach-1 px-4 flex items-center justify-between shrink-0">
            <p className="text-md font-bold text-grey-black tracking-[0.3px]">{title}</p>
            {showCloseButton ? (
              <button
                type="button"
                onClick={onClose}
                className="w-10 h-10 -mr-2 rounded-md flex items-center justify-center hover:bg-peach transition-colors"
                aria-label="Close"
              >
                <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M12 4L4 12M4 4l8 8"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            ) : null}
          </div>
        ) : showCloseButton ? (
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 z-10 w-10 h-10 rounded-full flex items-center justify-center bg-peach text-grey-black hover:bg-peach-1 transition-colors shadow-sm"
            aria-label="Close"
          >
            <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
              <path
                d="M12 4L4 12M4 4l8 8"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        ) : null}

        <div className="flex-1 min-h-0">{children}</div>

        {footer ? (
          <div className="shrink-0 border-t border-peach-1 bg-white px-4 py-3 pb-[calc(env(safe-area-inset-bottom)+12px)]">
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    document.body
  );
}
